/**
 * ncalcGenerator.js — Blockly workspace → NCalc `Expression` compiler for the Tax Calculate
 * block editor (S1-P7, task 3-A). It is the successor of `utils/formulaGraph.js`'s
 * `graphToExpression` / `graphToFormulas` and MUST emit strings **byte-identical** to them
 * (AC4 zero-drift — the single most important correctness property of this slice).
 *
 * PURE ESM. Imports Blockly ONLY through the cross-env shim `./blocklyCore.js` (NEVER
 * `blockly/core` directly). NO DOM: every function runs HEADLESS under `node tests/*.mjs`
 * over a `new Blockly.Workspace()` (never `Blockly.inject`).
 *
 * ── Precedence / paren rule (mirrors formulaGraph.js:36-41,163-187,228-229) ──────────────
 * A Blockly value tree has exactly ONE child per input, no fan-out, no cycles — so the compiler
 * is a plain recursion `emit(block) → { str, prec }`. The paren rule is reproduced EXACTLY:
 *   • Bands (higher binds tighter): atom/leaf/func-call = Infinity; `* /` = 3; `+ -` = 2; compare = 1.
 *   • A LEFT child is wrapped iff `left.prec <  parentPrec`  (STRICT `<`).
 *   • A RIGHT child is wrapped iff `right.prec <= parentPrec` (`<=`).
 *     → left-assoc pretty-print: `a - b - c` bare, `a - (b - c)` wrapped; `a / b / c` bare, `a / (b / c)` wrapped.
 *   • Function args are emitted at TOP level (never wrapped) — `IF(age >= 65, …)`, `MIN(a, MAX(b, c))`.
 *     `SUM(<iterated expression>)` is one of them: an atom of arity exactly 1, never nested.
 *   • Formatting locks: `String(Number(v))` → `500000` (no `.0`), `0.3`, `-5`; `==`/`!=` (never `=`/`<>`);
 *     single-space `A op B`; upper-case `MIN/MAX/ROUND/IF`; `, ` argument separator; SINGLE-quoted
 *     string literals — `'FEMALE'` (never `"FEMALE"`), verbatim content, no escaping (there are no
 *     escape sequences to write: the literal grammar has no backslash and no quote — see formulaGraph).
 *
 * We deliberately do NOT use Blockly's `valueToCode` floor-based wrapping (it over-parenthesises
 * left-assoc chains and cannot express the strict-left / ≤-right asymmetry). A `Blockly.Generator`
 * instance is exposed for API shape only; the public compile path uses `emit` directly.
 */
import Blockly from './blocklyCore.js'
// The string-literal grammar lives with the tokenizer that parses it back (formulaGraph). Imported,
// never re-spelled: emit and tokenize disagreeing is exactly the drift that breaks the round-trip.
import {
  isSafeStringLiteral, MAX_STRING_LITERAL_LEN, isRetiredIdentifier, retiredIdentifierMessage,
  IFS_FN, SUM_FN, parsePathName, parseExpression, analyzeIteration,
} from '../formulaGraph.js'

export const GRAPH_VERSION = 2
export const MAX_EXPRESSION_LEN = 1000

// Operator symbol + precedence tables (mirror formulaGraph.js exactly). Higher binds tighter.
const OP_SYM = { ADD: '+', SUB: '-', MUL: '*', DIV: '/' }
const OP_PREC = { ADD: 2, SUB: 2, MUL: 3, DIV: 3 }
const CMP_SYM = { LT: '<', LE: '<=', GT: '>', GE: '>=', EQ: '==', NE: '!=' }
const CMP_PREC = 1
const ATOM_PREC = Infinity

const VARIADIC_FN = { tax_func_min: 'MIN', tax_func_max: 'MAX' }

// The per-formula compile anchor (taxBlocks' TAX_FORMULA_RESULT_TYPE). Spelled here rather than
// imported: this module compiles any workspace and deliberately owns no block definitions.
const FORMULA_RESULT_TYPE = 'tax_formula_result'

// ──────────────────────────────────────────────────────────────────────────────
//  Core recursion:  emit(block) → { str, prec }   (mirrors graphToExpression's emit)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * @param {Object} block   a Blockly block (or null for an empty socket)
 * @param {{errors:string[]}} ctx  shared error sink (messages WITHOUT the [outputVar] prefix)
 * @returns {{str:string, prec:number}}
 */
function emit(block, ctx) {
  if (!block) {
    ctx.errors.push('ช่อง operand ว่าง (empty input)')
    return { str: '', prec: ATOM_PREC }
  }
  switch (block.type) {
    case 'tax_number': return emitNumber(block, ctx)
    case 'tax_text': return emitText(block, ctx)
    case 'tax_variable': return emitVariable(block, ctx)
    case 'tax_path': return emitPath(block, ctx)
    case 'tax_bool': return emitBool(block, ctx)
    case 'tax_func_sum': return emitSum(block, ctx)
    case 'tax_constant': return emitConstant(block, ctx)
    case 'tax_arith': return emitBinary(block, ctx, 'arith')
    case 'tax_compare': return emitBinary(block, ctx, 'compare')
    case 'tax_func_min':
    case 'tax_func_max': return emitVariadic(block, ctx)
    case 'tax_func_round': return emitRound(block, ctx)
    case 'tax_func_if': return emitIf(block, ctx)
    case 'tax_func_ifs': return emitIfs(block, ctx)
    case 'tax_define':
      ctx.errors.push('บล็อก define ต่อเป็น operand ไม่ได้ (define cannot be an operand)')
      return { str: '', prec: ATOM_PREC }
    case FORMULA_RESULT_TYPE:
      // Structurally unreachable (no output connection → it can never sit in a socket); kept so a
      // future caller that hands one here gets the reason instead of "ชนิดบล็อกไม่รู้จัก".
      ctx.errors.push('บล็อกผลลัพธ์ต่อเป็น operand ไม่ได้ (result block cannot be an operand)')
      return { str: '', prec: ATOM_PREC }
    default:
      ctx.errors.push('ชนิดบล็อกไม่รู้จัก: "' + block.type + '"')
      return { str: '', prec: ATOM_PREC }
  }
}

function emitNumber(block, ctx) {
  const raw = block.getFieldValue('VALUE')
  const v = Number(raw)
  if (raw == null || raw === '' || !Number.isFinite(v)) {
    ctx.errors.push('tax_number ไม่มีค่าที่ถูกต้อง (value)')
    return { str: '', prec: ATOM_PREC }
  }
  return { str: formatNumber(v), prec: ATOM_PREC }
}

function emitText(block, ctx) {
  const raw = block.getFieldValue('TEXT')
  const text = raw == null ? '' : String(raw)
  if (text === '') {
    ctx.errors.push('บล็อกข้อความยังไม่มีค่า (empty text literal)')
    return { str: '', prec: ATOM_PREC }
  }
  // Second gate on the SAME grammar the field validator applies. The field cannot normally produce an
  // illegal value — but a hand-edited stored blob can, and emitting it would ship an expression the
  // backend rejects at save with a message about a block the author never sees.
  if (!isSafeStringLiteral(text)) {
    ctx.errors.push(
      'ข้อความ "' + text + '" มีอักขระที่ใช้ไม่ได้ หรือยาวเกิน ' + MAX_STRING_LITERAL_LEN
      + ' ตัวอักษร (ใช้ได้: ตัวอักษร ตัวเลข เว้นวรรค _ - . /)',
    )
    return { str: '', prec: ATOM_PREC }
  }
  // Single quotes, content verbatim — the grammar contains neither a quote nor a backslash, so there
  // is nothing to escape and a literal can never break out of its quotes.
  return { str: "'" + text + "'", prec: ATOM_PREC }
}

function emitVariable(block, ctx) {
  const name = block.getFieldValue('NAME')
  if (name == null || String(name).trim() === '') {
    ctx.errors.push('tax_variable ไม่มีชื่อตัวแปร (name)')
    return { str: '', prec: ATOM_PREC }
  }
  // item_amount / item_rate / item_expense are RETIRED (they were bound only inside a SUMITEMS body,
  // and SUMITEMS is gone). The C# evaluator refuses them at BOTH save and run time, so emitting one
  // would ship an expression that 400s naming a variable the author cannot even drag from a flyout
  // any more. Refused here with the migration sentence, which names the path to write instead.
  if (isRetiredIdentifier(name)) {
    ctx.errors.push(retiredIdentifierMessage(name))
    // Blank, like the empty-body refusals: an errored compile must not ALSO hand back a
    // plausible-looking string. Nothing can save it either way (the view gates on errors AND on a
    // non-empty expression), but the preview strip would otherwise render the half-formula next to
    // its own red error — reading as if the expression were merely unfinished rather than refused.
    return { str: '', prec: ATOM_PREC }
  }
  // source (block.getTaxSource()) is DISPLAY-ONLY and NEVER affects the compiled string.
  return { str: String(name), prec: ATOM_PREC }
}

/**
 * A nested PATH leaf — `INCOME_40_5.subItems[].items[].amount`, emitted VERBATIM.
 *
 * The canonical text is re-parsed through the grammar's own `parsePathName` before it is emitted, and
 * the emitted string is the CANONICAL re-spelling of what came back — not the stored string. That is
 * what makes the round trip byte-exact by construction: whatever spelling reached the block (a stored
 * blob, a paste, a flyout), what leaves is the one form `tokenize` produces, so recompiling a graph can
 * never differ from the persisted Expression by a character the author cannot see.
 *
 * A path that does NOT parse is refused, blank — the same call every other refusal in this file makes.
 * Emitting it anyway would ship an expression the backend rejects at save, naming a path the author
 * never typed a character of (the block shows `a ▸ b[] ▸ c`, the error would quote `a.b[].c`).
 */
function emitPath(block, ctx) {
  const raw = typeof block.getTaxPath === 'function' ? block.getTaxPath() : ''
  const path = raw == null ? '' : String(raw).trim()
  if (path === '') {
    ctx.errors.push('บล็อกเส้นทางข้อมูลยังไม่ได้เลือกเส้นทาง (path ว่าง)')
    return { str: '', prec: ATOM_PREC }
  }
  const segs = parsePathName(path)
  if (!segs) {
    ctx.errors.push(
      'เส้นทางข้อมูล "' + path + '" เขียนไม่ถูกต้อง — ใช้รูปแบบ ชื่อ.ชื่อ[].ชื่อ เท่านั้น'
      + ' (ห้ามเว้นวรรค ห้ามใส่เลขในวงเล็บเหลี่ยม เช่น items[0])',
    )
    return { str: '', prec: ATOM_PREC }
  }
  return { str: segs.map((g, i) => (i === 0 ? g.name : '.' + g.name) + (g.iter ? '[]' : '')).join(''), prec: ATOM_PREC }
}

/** TRUE / FALSE. Upper-case, always — one spelling in, one spelling out (the round-trip contract). */
function emitBool(block, ctx) {
  const v = String(block.getFieldValue('VALUE') || '').toUpperCase()
  if (v !== 'TRUE' && v !== 'FALSE') {
    ctx.errors.push('บล็อก TRUE/FALSE มีค่าที่ไม่รู้จัก: "' + v + '"')
    return { str: '', prec: ATOM_PREC }
  }
  return { str: v, prec: ATOM_PREC }
}

/**
 * `IFS(cond1, val1, …)` — the arguments are PAIRS and the pairing is the meaning: argument 2k is a
 * condition and 2k+1 is the value taken when it is the FIRST true one. A half-filled pair therefore
 * cannot be emitted as "the rest of the call": dropping the empty socket would shift every later
 * argument by one and silently turn conditions into values. So a missing socket refuses the whole call.
 */
function emitIfs(block, ctx) {
  const pairs = ifsPairs(block)
  if (pairs.length < 1) {
    ctx.errors.push(IFS_FN + '() ต้องมีอย่างน้อย 1 คู่ (เงื่อนไข, ค่า) — กดฟันเฟืองเพื่อเพิ่มคู่')
    return { str: '', prec: ATOM_PREC }
  }
  const args = []
  let missing = false
  pairs.forEach((i) => {
    const c = block.getInputTargetBlock('COND' + i)
    const v = block.getInputTargetBlock('VAL' + i)
    if (!c || !v) { missing = true; return }
    args.push(emit(c, ctx).str)
    args.push(emit(v, ctx).str)
  })
  if (missing) {
    ctx.errors.push(IFS_FN + '() มีคู่เงื่อนไข/ค่าที่ยังไม่ได้ใส่ให้ครบ — ต้องใส่ทั้งเงื่อนไขและค่าของทุกคู่')
    return { str: '', prec: ATOM_PREC }
  }
  return { str: IFS_FN + '(' + args.join(', ') + ')', prec: ATOM_PREC }
}

/** Ordered pair indices `0..n` for a tax_func_ifs block (COND<i>/VAL<i> input pairs). */
function ifsPairs(block) {
  const idx = (block.inputList || [])
    .map((i) => i && i.name)
    .filter((n) => /^COND\d+$/.test(String(n)))
    .map((n) => Number(String(n).slice(4)))
  idx.sort((a, b) => a - b)
  return idx
}

/**
 * `SUM(<iterated expression>)` — arity EXACTLY 1, never nested.
 *
 * The Σ block carries NO scope and NO fields: which rows are walked is already stated by the `[]`
 * paths inside it. All this block says is WHERE the summing happens — the one thing a `[]` path
 * cannot say for itself, and the reason `MIN(path * 0.5, 100000)` is refused (see analyzeIteration).
 *
 * The "there must be a `[]` inside" rule is NOT checked here: it is a property of the compiled
 * expression, so it is checked once, on the parsed result, by compileExpression → analyzeIteration.
 * Two implementations of one rule is how the block editor and the paste dialog come to disagree.
 */
function emitSum(block, ctx) {
  // Nesting is refused by the parser too; refusing it here as well is what keeps the generator from
  // emitting a string its own parser cannot read back (the round-trip contract).
  if (ctx.sumDepth > 0) {
    ctx.errors.push('ซ้อนบล็อก Σ (' + SUM_FN + ') ใน Σ อีกชั้นไม่ได้ — Σ ชั้นในรวมรายการเป็นค่าเดียวไปแล้ว')
    return { str: '', prec: ATOM_PREC }
  }
  const bodyBlock = block.getInputTargetBlock('VALUE')
  if (!bodyBlock) {
    ctx.errors.push('บล็อก Σ ยังไม่ได้ใส่สูตรที่จะคิดต่อ 1 รายการ (' + SUM_FN + ' ว่าง)')
    return { str: '', prec: ATOM_PREC }
  }
  const errorsBefore = ctx.errors.length
  ctx.sumDepth = (ctx.sumDepth || 0) + 1
  let body
  try {
    body = emit(bodyBlock, ctx)
  } finally {
    ctx.sumDepth -= 1
  }
  // A body that failed cannot make a meaningful aggregate, so emit NOTHING rather than `SUM()` — the
  // same call emitVariadic makes for a half-filled MIN. Errors already block the save; what this buys
  // is that the half-formula never exists as a string anyone can quote.
  if (ctx.errors.length !== errorsBefore) return { str: '', prec: ATOM_PREC }
  // A function call is an ATOM, and its arg is emitted at TOP level — identical to MIN/MAX/ROUND/IF.
  return { str: SUM_FN + '(' + body.str + ')', prec: ATOM_PREC }
}

function emitConstant(block, ctx) {
  const key = block.getFieldValue('CONSTKEY')
  if (key == null || String(key).trim() === '') {
    ctx.errors.push('tax_constant ไม่มี constKey')
    return { str: '', prec: ATOM_PREC }
  }
  // Constant compiles to its KEY only — the value is persisted separately, NEVER inlined.
  return { str: String(key), prec: ATOM_PREC }
}

function emitBinary(block, ctx, kind) {
  const code = block.getFieldValue('OP')
  let sym
  let prec
  if (kind === 'compare') {
    sym = CMP_SYM[code]
    prec = CMP_PREC
    if (!sym) { ctx.errors.push('comparator ไม่รู้จัก: "' + code + '"'); return { str: '', prec: CMP_PREC } }
  } else {
    sym = OP_SYM[code]
    prec = OP_PREC[code]
    if (!sym) { ctx.errors.push('operator ไม่รู้จัก: "' + code + '"'); return { str: '', prec: 2 } }
  }
  const aBlock = block.getInputTargetBlock('A')
  const bBlock = block.getInputTargetBlock('B')
  if (!aBlock || !bBlock) {
    const label = kind === 'compare' ? 'comparison' : 'operator'
    ctx.errors.push(label + ' ต้องมี 2 input (A, B) — ช่องว่าง')
    return { str: '', prec }
  }
  const left = emit(aBlock, ctx)
  const right = emit(bBlock, ctx)
  // Left-assoc pretty-print: left wraps only when strictly lower prec; right wraps when lower-or-equal.
  const l = left.prec < prec ? '(' + left.str + ')' : left.str
  const r = right.prec <= prec ? '(' + right.str + ')' : right.str
  return { str: l + ' ' + sym + ' ' + r, prec }
}

function emitVariadic(block, ctx) {
  const fn = VARIADIC_FN[block.type]
  const inputs = variadicInputs(block)
  if (inputs.length < 2) {
    ctx.errors.push(fn + '() ต้องมีอย่างน้อย 2 ช่อง (ตอนนี้มี ' + inputs.length + ') — กดฟันเฟืองเพื่อเพิ่มช่อง')
    return { str: '', prec: ATOM_PREC }
  }
  const args = []
  let missing = false
  inputs.forEach((name) => {
    const child = block.getInputTargetBlock(name)
    if (!child) { missing = true; return }
    args.push(emit(child, ctx).str)
  })
  if (missing) {
    ctx.errors.push(fn + '() มีช่องที่ยังไม่ได้ใส่ค่า')
    return { str: '', prec: ATOM_PREC }
  }
  // FLAT single call — never nested (MIN(a, b, c), never MIN(a, MIN(b, c))).
  return { str: fn + '(' + args.join(', ') + ')', prec: ATOM_PREC }
}

function emitRound(block, ctx) {
  const xBlock = block.getInputTargetBlock('X')
  if (!xBlock) {
    ctx.errors.push('ROUND() ยังไม่ได้ใส่ค่าที่จะปัดเศษ')
    return { str: '', prec: ATOM_PREC }
  }
  const args = [emit(xBlock, ctx).str]
  const nBlock = block.getInputTargetBlock('N')
  if (nBlock) args.push(emit(nBlock, ctx).str)
  return { str: 'ROUND(' + args.join(', ') + ')', prec: ATOM_PREC }
}

function emitIf(block, ctx) {
  const cond = block.getInputTargetBlock('COND')
  const then = block.getInputTargetBlock('THEN')
  const els = block.getInputTargetBlock('ELSE')
  if (!cond || !then || !els) {
    ctx.errors.push('IF() ต้องใส่ให้ครบทั้ง 3 ช่อง (เงื่อนไข / ถ้าใช่ / ถ้าไม่ใช่)')
    return { str: '', prec: ATOM_PREC }
  }
  const args = [emit(cond, ctx).str, emit(then, ctx).str, emit(els, ctx).str]
  return { str: 'IF(' + args.join(', ') + ')', prec: ATOM_PREC }
}

/** Ordered `ADD0..ADDn` value-input names for a variadic MIN/MAX block. */
function variadicInputs(block) {
  const names = (block.inputList || [])
    .map((i) => i && i.name)
    .filter((n) => /^ADD\d+$/.test(String(n)))
  names.sort((a, b) => Number(a.slice(3)) - Number(b.slice(3)))
  return names
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: single-block / single-subtree compile
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Compile one value subtree to `{ expression, errors }` (mirror of graphToExpression's return).
 * @param {Object} rootBlock  the value block plugged into a define's EXPR socket
 * @returns {{ expression:string, errors:string[] }}
 */
export function compileExpression(rootBlock) {
  const ctx = { errors: [] }
  if (!rootBlock) {
    ctx.errors.push('define ต้องมี 1 input (EXPR ว่าง)')
    return { expression: '', errors: dedupe(ctx.errors) }
  }
  // A formula ALWAYS returns a number: a string is an INPUT to a comparison, never a result (the engine
  // puts every formula's value into a decimal bag). Blockly cannot express this — tax_text has no output
  // connection to type-check against at a ROOT — so the one place that knows "this subtree is the whole
  // formula" says it, rather than persisting an Expression that 400s at calculation time for someone else.
  if (rootBlock.type === 'tax_text') {
    ctx.errors.push('สูตรต้องคืนค่าเป็นตัวเลข — บล็อกข้อความใช้ได้เฉพาะเป็นตัวเทียบในเงื่อนไข (string cannot be a formula result)')
    return { expression: '', errors: dedupe(ctx.errors) }
  }
  const r = emit(rootBlock, ctx)
  const expression = r.str
  if (expression.length > MAX_EXPRESSION_LEN) {
    ctx.errors.push('สูตรยาวเกิน ' + MAX_EXPRESSION_LEN + ' อักขระ (' + expression.length + '/' + MAX_EXPRESSION_LEN + ') — ลดการต่อซ้ำ')
  }
  // The two STRUCTURAL `[]` rules — uncorrelated loops, and a function wrapping the whole iterated
  // computation with no explicit Σ (see formulaGraph.analyzeIteration). Run over the PARSED emitted
  // string rather than over the block tree on purpose: it is the same function the paste dialog and
  // the lint call, so the canvas cannot be allowed to build something the text path would refuse (or
  // the other way round). Skipped when the compile already failed — a half-formula has no shape to
  // judge, and a second red line about it would only bury the first.
  if (ctx.errors.length === 0 && ITERATION_RELEVANT.test(expression)) {
    iterationErrors(expression).forEach((m) => ctx.errors.push(m))
  }
  return { expression, errors: dedupe(ctx.errors) }
}

// The cheap "is there anything for analyzeIteration to say" test. A `[` means an iteration point; a
// `SUM(` means a stated boundary, which has its own rules (it must contain one). Anything else cannot
// trip either rule, and skipping the parse for it keeps the live recompile cheap.
const ITERATION_RELEVANT = /\[|\bSUM\s*\(/i

/**
 * Parse `expression` and return its structural `[]` problems. A string this module just emitted
 * always parses — if it somehow does not, that is a generator bug and NOT something to report as an
 * iteration problem, so the throw is swallowed and the round-trip test is what catches it.
 * @param {string} expression
 * @returns {string[]}
 */
function iterationErrors(expression) {
  try {
    return analyzeIteration(parseExpression(expression))
  } catch (_err) {
    return []
  }
}

/**
 * Single-block emit for the live compiled-expression bar (task 5-A). A `tax_define` compiles its
 * EXPR child; any value block compiles its own subtree. Returns the NCalc string only.
 * @param {Object} block
 * @returns {string}
 */
export function blockToNCalc(block) {
  if (!block) return ''
  if (block.type === 'tax_define') {
    return compileExpression(block.getInputTargetBlock('EXPR')).expression
  }
  return emit(block, { errors: [] }).str
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: PER-FORMULA workspace compile (Formula level — one expression tree)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Compile a per-formula workspace → `{ expression, errors }`. The Formula editor's workspace holds ONE
 * expression tree (no `tax_define` / START–FINAL spine — that metadata lives on the Formula List row).
 *
 * `tax_formula_result` is the compile ANCHOR: its VALUE subtree is the formula, and every other
 * top-level block is the author's scratch space — ignored, never an error. Free-standing blocks are how
 * a formula gets built (park a subtree, try another, plug the winner in), so rejecting them rejected
 * the work in progress.
 *
 * An empty anchor is an ERROR, never an empty string: a half-formula persisted here would sit in the
 * DB and 400 at calculation time for whoever runs it.
 * @param {Object} workspace  a Blockly workspace (headless or rendered)
 * @returns {{ expression:string, errors:string[] }}
 */
export function workspaceToExpression(workspace) {
  const tops = getTopBlocks(workspace).filter(
    (b) => b && !(typeof b.isInsertionMarker === 'function' && b.isInsertionMarker()),
  )
  const anchors = tops.filter((b) => b.type === FORMULA_RESULT_TYPE)

  if (anchors.length > 1) {
    return {
      expression: '',
      errors: ['มีบล็อกผลลัพธ์ ' + anchors.length + ' ตัว — หนึ่งสูตรมีบล็อกผลลัพธ์ได้ตัวเดียว'],
    }
  }
  if (anchors.length === 1) {
    const value = anchors[0].getInputTargetBlock('VALUE')
    if (!value) {
      return { expression: '', errors: ['ยังไม่ได้เสียบสูตรเข้าบล็อกผลลัพธ์ — บล็อกที่เสียบในช่องนี้เท่านั้นที่ถูกคำนวณ'] }
    }
    return compileExpression(value)
  }

  // ── Pre-result-block compat path (NOT a feature) ──────────────────────────────────
  // Every ExpressionGraphJson stored before tax_formula_result existed is a BARE single-root tree.
  // chooseFormulaSource only honours a stored graph that recompiles to the persisted Expression, so if
  // this path demanded an anchor, every legacy graph would fail that check and silently degrade to a
  // rebuild — discarding the layout the author arranged, without telling them. The editor itself never
  // reaches here: ensureResultRoot() gives every workspace an anchor before it is compiled.
  const roots = tops.filter((b) => b.outputConnection)
  if (roots.length === 0) {
    return { expression: '', errors: ['ยังไม่มีสูตร — ลากบล็อกมาประกอบเป็นสูตร (empty formula)'] }
  }
  if (roots.length > 1) {
    return {
      expression: '',
      errors: ['มีบล็อกลอย ' + roots.length + ' ตัว แต่ไม่มีบล็อกผลลัพธ์ — เสียบสูตรที่ต้องการเข้าบล็อกผลลัพธ์'],
    }
  }
  return compileExpression(roots[0])
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: whole-workspace compile → one formula per tax_define
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Compile the whole workspace → one formula per `tax_define` block (mirror of graphToFormulas).
 * @param {Object} workspace  a Blockly workspace (headless or rendered)
 * @returns {{ formulas:Array<{outputVar:string,expression:string,appliesToKind:string,appliesToKey:string,evalOrder:number,notes:?string}>, errors:string[] }}
 */
export function workspaceToFormulas(workspace) {
  const errors = []
  const allBlocks = getAllBlocks(workspace)
  const defines = orderedDefines(workspace)

  if (defines.length === 0) {
    errors.push('ไม่มีบล็อก define ในแคนวาส (missing define)')
    integrityErrors(allBlocks).forEach((m) => errors.push(m))
    return { formulas: [], errors: dedupe(errors) }
  }

  // duplicate outputVar + defined-var set
  const seen = new Set()
  const definedVars = new Set()
  defines.forEach((d) => {
    const v = outputVarOf(d)
    if (v === '') { errors.push('define ไม่มีชื่อ outputVar'); return }
    if (seen.has(v)) errors.push('outputVar ซ้ำ: "' + v + '" (duplicate outputVar)')
    else { seen.add(v); definedVars.add(v) }
  })

  const formulas = []
  const depGraph = new Map() // outputVar -> Set(referenced outputVars)
  defines.forEach((d, idx) => {
    const meta = readMeta(d)
    const outputVar = meta.outputVar
    const exprBlock = d.getInputTargetBlock('EXPR')
    const r = compileExpression(exprBlock)
    r.errors.forEach((msg) => errors.push((outputVar ? '[' + outputVar + '] ' : '') + msg))

    // OutputVar-source variable leaves in THIS formula's EXPR subtree → dependency edges + orphan
    // scan. Scoped to the EXPR child (not d.getDescendants) so a chained next-define never bleeds in.
    const deps = new Set()
    ;(exprBlock ? exprBlock.getDescendants(false) : []).forEach((n) => {
      if (n && n.type === 'tax_variable' && typeof n.getTaxSource === 'function' && n.getTaxSource() === 'OutputVar') {
        const nm = String(n.getFieldValue('NAME') || '')
        if (nm === '') return
        if (!definedVars.has(nm)) errors.push('outputVar leaf อ้างถึง define ที่ไม่มี: "' + nm + '" (orphan outputVar)')
        else deps.add(nm) // includes self-reference → self-cycle
      }
    })
    if (outputVar) depGraph.set(outputVar, deps)

    formulas.push({
      outputVar,
      expression: r.expression,
      appliesToKind: meta.appliesToKind || 'RULE_CODE',
      appliesToKey: meta.appliesToKey != null ? String(meta.appliesToKey) : '',
      evalOrder: Number.isFinite(Number(meta.evalOrder)) ? Number(meta.evalOrder) : idx,
      notes: meta.notes != null ? meta.notes : null,
    })

    setWarn(d, r.errors.length ? r.errors.join(' · ') : null)
  })

  // cross-output cycle
  detectCrossOutputCycles(depGraph).forEach((m) => errors.push(m))

  // version-level integrity (constants, brackets)
  integrityErrors(allBlocks).forEach((m) => errors.push(m))

  formulas.sort((a, b) => (a.evalOrder - b.evalOrder) || String(a.outputVar).localeCompare(String(b.outputVar)))
  return { formulas, errors: dedupe(errors) }
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: aggregate Constant / Bracket blocks
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Aggregate `tax_constant` blocks → persistence array (dedup by UPPER(constKey), FIRST-wins;
 * value preserved exactly). Empty workspace → `[]`.
 * @param {Object} workspace
 * @returns {Array<{constKey:string,constValue:number,notes:?string}>}
 */
export function collectConstants(workspace) {
  const out = []
  const seen = new Set()
  getAllBlocks(workspace).forEach((b) => {
    if (!b || b.type !== 'tax_constant') return
    const rawKey = String(b.getFieldValue('CONSTKEY') || '').trim()
    if (rawKey === '') return
    const key = rawKey.toUpperCase()
    if (seen.has(key)) return // first-wins dedup
    seen.add(key)
    const cv = Number(b.getFieldValue('CONSTVALUE'))
    out.push({
      constKey: key,
      constValue: Number.isFinite(cv) ? cv : 0,
      // tax_constant carries no notes field in the block model → null (shape parity).
      notes: null,
    })
  })
  return out
}

/**
 * Aggregate `tax_bracket` block row(s) → persistence array, ordered by sortOrder then lowerBound.
 * Open-top rows keep `upperBound:null`. Empty workspace → `[]`.
 * @param {Object} workspace
 * @returns {Array<{lowerBound:number,upperBound:?number,rate:number,sortOrder:number}>}
 */
export function collectBrackets(workspace) {
  const rows = []
  getAllBlocks(workspace).forEach((b) => {
    if (!b || b.type !== 'tax_bracket') return
    const rs = typeof b.getBracketRows === 'function' ? b.getBracketRows() : []
    ;(Array.isArray(rs) ? rs : []).forEach((r) => {
      if (r == null) return
      rows.push({
        lowerBound: num(r.lowerBound),
        upperBound: r.upperBound == null ? null : num(r.upperBound),
        rate: num(r.rate),
        sortOrder: num(r.sortOrder),
      })
    })
  })
  rows.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.lowerBound - b.lowerBound))
  return rows
}

// ──────────────────────────────────────────────────────────────────────────────
//  Blockly.Generator('NCalc') — exposed for API shape only (public path uses emit()).
// ──────────────────────────────────────────────────────────────────────────────
export const ncalcGenerator = new Blockly.Generator('NCalc')
ncalcGenerator.ORDER_ATOMIC = 0
ncalcGenerator.ORDER_MUL_DIV = 3
ncalcGenerator.ORDER_ADD_SUB = 4
ncalcGenerator.ORDER_COMPARISON = 5
ncalcGenerator.ORDER_NONE = 99
;[
  'tax_number', 'tax_text', 'tax_variable', 'tax_path', 'tax_bool', 'tax_constant',
  'tax_arith', 'tax_compare',
  'tax_func_min', 'tax_func_max', 'tax_func_round', 'tax_func_if', 'tax_func_ifs', 'tax_func_sum',
].forEach((t) => {
  // Each handler fully compiles the block's subtree via emit() (correct parens baked in) and
  // reports it as ATOMIC so any wrapping generator never double-parenthesises.
  ncalcGenerator.forBlock[t] = (block) => [emit(block, { errors: [] }).str, ncalcGenerator.ORDER_ATOMIC]
})

// ──────────────────────────────────────────────────────────────────────────────
//  Internal helpers
// ──────────────────────────────────────────────────────────────────────────────
function readMeta(defineBlock) {
  if (typeof defineBlock.getTaxMeta === 'function') return defineBlock.getTaxMeta()
  return {
    outputVar: defineBlock.getFieldValue('OUTPUTVAR') || '',
    appliesToKind: 'RULE_CODE',
    appliesToKey: '',
    evalOrder: 0,
    notes: null,
  }
}

function outputVarOf(defineBlock) {
  const v = defineBlock && defineBlock.getFieldValue ? defineBlock.getFieldValue('OUTPUTVAR') : ''
  return v == null ? '' : String(v).trim()
}

/** Detect reference cycles among define blocks (via OutputVar leaves). Returns error messages. */
function detectCrossOutputCycles(depGraph) {
  const errs = []
  const WHITE = 0; const GRAY = 1; const BLACK = 2
  const color = new Map()
  depGraph.forEach((_v, k) => color.set(k, WHITE))
  const stack = []
  const dfs = (u) => {
    color.set(u, GRAY)
    stack.push(u)
    const deps = depGraph.get(u) || new Set()
    deps.forEach((v) => {
      if (!depGraph.has(v)) return
      if (color.get(v) === GRAY) {
        const idx = stack.indexOf(v)
        const cyc = stack.slice(idx).concat(v)
        errs.push('พบวงจรอ้างอิงข้าม Output (cross-output cycle): ' + cyc.join(' → '))
      } else if (color.get(v) === WHITE) {
        dfs(v)
      }
    })
    stack.pop()
    color.set(u, BLACK)
  }
  depGraph.forEach((_v, k) => { if (color.get(k) === WHITE) dfs(k) })
  return dedupe(errs)
}

/** Version-level integrity of tax_constant + tax_bracket blocks (dup key, >1 bracket, row validity). */
function integrityErrors(allBlocks) {
  const errs = []
  const list = Array.isArray(allBlocks) ? allBlocks : []

  const constKeys = new Set()
  list.forEach((b) => {
    if (!b || b.type !== 'tax_constant') return
    const k = String(b.getFieldValue('CONSTKEY') || '').trim()
    if (k === '') { errs.push('tax_constant ไม่มี constKey'); return }
    const up = k.toUpperCase()
    if (constKeys.has(up)) errs.push('constKey ซ้ำ: "' + up + '" (duplicate constant)')
    else constKeys.add(up)
  })

  const bracketBlocks = list.filter((b) => b && b.type === 'tax_bracket')
  if (bracketBlocks.length > 1) errs.push('มี Bracket block มากกว่า 1 (ต้องมีได้เวอร์ชันละ 1 บล็อก)')
  bracketBlocks.forEach((b) => {
    const rows = typeof b.getBracketRows === 'function' ? b.getBracketRows() : []
    validateBracketRows(rows).forEach((m) => errs.push(m))
  })
  return errs
}

/** Bracket-row integrity: negative rate, >1 open-top, overlap, invalid range. */
function validateBracketRows(rows) {
  const errs = []
  const rs = Array.isArray(rows) ? rows.slice() : []
  rs.forEach((r) => { if (r && num(r.rate) < 0) errs.push('bracket rate ติดลบ (rate < 0)') })
  const openTop = rs.filter((r) => r && r.upperBound == null)
  if (openTop.length > 1) errs.push('bracket มี open-top (upperBound:null) มากกว่า 1 แถว')
  const sorted = rs.slice().sort((a, b) => (num(a.sortOrder) - num(b.sortOrder)) || (num(a.lowerBound) - num(b.lowerBound)))
  for (let i = 0; i < sorted.length; i += 1) {
    const r = sorted[i]
    const lo = num(r.lowerBound)
    const hi = r.upperBound == null ? Infinity : num(r.upperBound)
    if (hi <= lo) errs.push('bracket ช่วงไม่ถูกต้อง (upperBound ต้อง > lowerBound) ที่ sortOrder ' + num(r.sortOrder))
    if (i + 1 < sorted.length) {
      const nlo = num(sorted[i + 1].lowerBound)
      if (hi > nlo) errs.push('bracket ซ้อนทับกัน (overlap) ที่ sortOrder ' + num(r.sortOrder))
    }
  }
  return errs
}

function getTopBlocks(workspace) {
  if (!workspace || typeof workspace.getTopBlocks !== 'function') return []
  return workspace.getTopBlocks(true) || []
}

/**
 * All `tax_define` blocks in evaluation (visual chain) order. Walks each `tax_start` hat's
 * next-chain first (top → bottom = eval order), then any stray define chains not under a START
 * (backward-compatible: loose top-level defines — as the headless tests build — still compile).
 * A tax_result cap in the chain is skipped. De-duplicated by block id.
 * @param {Object} workspace
 * @returns {Object[]} tax_define blocks
 */
export function orderedDefines(workspace) {
  const tops = getTopBlocks(workspace)
  const out = []
  const seen = new Set()
  const walk = (block) => {
    let b = block
    while (b) {
      if (b.type === 'tax_define' && !seen.has(b.id)) { seen.add(b.id); out.push(b) }
      b = typeof b.getNextBlock === 'function' ? b.getNextBlock() : null
    }
  }
  // 1) defines chained under a START hat (authoritative pipeline order)
  tops.filter((b) => b && b.type === 'tax_start').forEach((s) => walk(typeof s.getNextBlock === 'function' ? s.getNextBlock() : null))
  // 2) any define chain NOT under a START (loose blocks / legacy workspaces)
  tops.filter((b) => b && b.type === 'tax_define').forEach((d) => walk(d))
  return out
}

function getAllBlocks(workspace) {
  if (!workspace || typeof workspace.getAllBlocks !== 'function') return []
  return workspace.getAllBlocks(false) || []
}

function setWarn(block, text) {
  if (!block || typeof block.setWarningText !== 'function') return
  try { block.setWarningText(text) } catch (_err) { /* headless / not rendered — ignore */ }
}

function formatNumber(v) {
  // Integer → no ".0"; decimal preserved as JS default string (matches formulaGraph.formatNumber).
  return String(v)
}

function num(v, d = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : d
}

function dedupe(arr) {
  return Array.from(new Set(arr))
}

export default {
  GRAPH_VERSION,
  MAX_EXPRESSION_LEN,
  workspaceToExpression,
  workspaceToFormulas,
  orderedDefines,
  collectConstants,
  collectBrackets,
  blockToNCalc,
  compileExpression,
  ncalcGenerator,
}
